/**
 * Privacy gate admin checklist (spec §9.6). Rendered wherever the proof
 * recording entry point is used while PROOF_VIDEO_ENABLED is false: the
 * complete consent/privacy checklist with the honest status
 * 'nog niet geactiveerd' instead of a recording UI.
 */
import { Banner, StatusChip } from '@/components/ui'
import { CheckIcon, LockIcon, ShieldIcon } from '@/components/icons'

export type PrivacyGateItem = {
  id: string
  title: string
  detail: string
}

/** The exact §9.6 checklist, in Dutch. */
export const PRIVACY_GATE_ITEMS: readonly PrivacyGateItem[] = [
  {
    id: 'retention',
    title: 'Bewaartermijn gekozen',
    detail: 'Er is een expliciete bewaartermijn voor bewijsvideo’s vastgelegd.',
  },
  {
    id: 'consent-copy',
    title: 'Toestemmingstekst toegevoegd',
    detail: 'De cliënt ziet vóór elke opname wat wordt opgenomen, wie kan meekijken en hoelang de video bewaard blijft.',
  },
  {
    id: 'privacy-notice',
    title: 'Privacyverklaring bijgewerkt',
    detail: 'De verwerking van bewijsvideo’s staat in de privacyverklaring.',
  },
  {
    id: 'roles',
    title: 'Rollen bevestigd',
    detail: 'Verwerkingsverantwoordelijke en verwerker zijn contractueel vastgelegd.',
  },
  {
    id: 'deletion',
    title: 'Verwijdering geverifieerd',
    detail: 'Verwijderen van een video verwijdert ook het opgeslagen bestand, getest end-to-end.',
  },
  {
    id: 'export',
    title: 'Data-export geverifieerd',
    detail: 'Bewijsvideo’s zitten in de export van eigen gegevens, getest end-to-end.',
  },
  {
    id: 'audit',
    title: 'Toegangsaudit geverifieerd',
    detail: 'Elke weergave van een bewijsvideo wordt vastgelegd in het auditlog.',
  },
  {
    id: 'no-public-url',
    title: 'Geen openbare URL',
    detail: 'Video’s zijn alleen bereikbaar via kortdurende, ondertekende links.',
  },
  {
    id: 'no-third-party-ai',
    title: 'Geen AI-analyse door derden',
    detail: 'Video’s worden nooit door externe diensten geanalyseerd en nooit gebruikt om automatisch te beoordelen of medicatie is ingenomen.',
  },
] as const

export function ProofPrivacyGatePanel() {
  return (
    <div className="proof-gate">
      <Banner tone="info">
        Bewijsvideo is <strong>nog niet geactiveerd</strong>. De techniek is klaar; de privacy-checklist
        hieronder moet eerst volledig zijn afgerond.
      </Banner>
      <div className="proof-gate-header">
        <span className="proof-consent-icon" aria-hidden="true">
          <ShieldIcon size={22} />
        </span>
        <h3 className="proof-consent-title">Privacy-checklist bewijsvideo</h3>
      </div>
      <ul className="proof-gate-list">
        {PRIVACY_GATE_ITEMS.map((item) => (
          <li key={item.id} className="proof-gate-item">
            <span className="proof-gate-item-icon" aria-hidden="true">
              <CheckIcon size={16} />
            </span>
            <span className="proof-gate-item-text">
              <span className="proof-gate-item-title">{item.title}</span>
              <span className="proof-gate-item-detail">{item.detail}</span>
            </span>
            <StatusChip status="neutral">
              <LockIcon size={14} /> Nog niet geactiveerd
            </StatusChip>
          </li>
        ))}
      </ul>
      <p className="proof-text proof-text--muted">
        Zodra alle punten zijn bevestigd kan de functie worden geactiveerd. Tot die tijd kan er geen
        video worden opgenomen of geüpload.
      </p>
    </div>
  )
}
